/**
 * FixedLanguageToggle — guest-page control cluster, fixed top-end.
 *
 * Renders the ThemeSwitcher beside the globe language switcher as a flex cluster,
 * so guests switch theme and language from one place.
 *
 * Fixed position (top-4 end-4), z-50, never scrolls with page.
 * Dropdown shows AR / EN options on click.
 * Active language gets terracotta underline accent.
 *
 * Auth-aware: calls PATCH /auth/me/ when authenticated, i18n.changeLanguage()
 * always runs for instant UI feedback. On failure, reverts i18n.
 *
 * @layer-1 — shared UI primitive.
 */
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useAuthContext } from "@/shared/hooks/useAuthContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateUserPreferences } from "@/features/settings/api/settingsApi";
import { settingsKeys } from "@/shared/lib/queryKeys";
import { useReducedMotion } from "@/shared/hooks/useReducedMotion";
import { useLangTransition } from "@/shared/hooks/useLangTransition";
import { ThemeSwitcher } from "@/shared/components/ThemeSwitcher";

export function FixedLanguageToggle() {
  const { i18n } = useTranslation();
  const auth = useAuthContext();
  const qc = useQueryClient();
  const prefersReducedMotion = useReducedMotion();
  const { triggerTransition } = useLangTransition();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { mutate: syncLanguage, isPending: isSyncing } = useMutation({
    mutationFn: (lang: "ar" | "en") => updateUserPreferences({ language: lang }),
    onSuccess: (_data, lang) => {
      qc.setQueryData(settingsKeys.preferences(), (old: unknown) => {
        if (old && typeof old === "object" && !Array.isArray(old)) {
          return { ...(old as Record<string, unknown>), language: lang };
        }
        return old;
      });
    },
    onError: (_err, lang) => {
      i18n.changeLanguage(lang === "ar" ? "en" : "ar");
    },
  });

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleLanguageChange = (lang: "ar" | "en") => {
    if (lang === i18n.language) { setOpen(false); return; }
    setOpen(false);
    triggerTransition(() => {
      i18n.changeLanguage(lang);
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
      if (auth.status === "authenticated") {
        syncLanguage(lang);
      }
    });
  };

  return (
    <div ref={ref} className="absolute top-4 end-4 z-50 flex items-center gap-2">
      <ThemeSwitcher />
      <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Toggle language"
        aria-expanded={open}
        disabled={isSyncing}
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg transition-colors cursor-pointer",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
          "focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          !prefersReducedMotion && "active:scale-95",
          open && !prefersReducedMotion && "animate-optionSelect",
          open
            ? "bg-sidebar-primary text-sidebar-primary-foreground"
            : "bg-sidebar-bg text-sidebar-text hover:bg-sidebar-hover-bg",
        )}
      >
        <Globe className="h-4 w-4" />
      </button>

      {open && (
        <div
          className={cn(
            "absolute end-0 top-full mt-2 min-w-[6rem] rounded-xl border shadow-warm",
            "bg-sidebar-bg overflow-hidden",
            "border-sidebar-border",
          )}
        >
          {(["ar", "en"] as const).map((lang) => {
            const isActive = i18n.language === lang;
            return (
              <button
                key={lang}
                type="button"
                onClick={() => handleLanguageChange(lang)}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer",
                  isActive
                    ? "bg-sidebar-active-bg font-semibold text-sidebar-text-active"
                    : "text-sidebar-text hover:bg-sidebar-hover-bg",
                )}
              >
                <span className="text-xs font-medium uppercase">{lang}</span>
                {isActive && (
                  <span
                    className="ms-auto h-0.5 w-4 rounded-full"
                    style={{ background: "var(--accent-gold)" }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}